"use client"

import { Head, router } from "@inertiajs/react"
import { MusicNote01, Plus, SearchLg } from "@untitledui/icons"
import { AnimatePresence, motion } from "motion/react"
import { useMemo, useState } from "react"

import { PlaylistCard } from "@/components/playlists"
import { Button } from "@/components/ui/button"
import { DestructiveStackedLeftAlignedModal } from "@/components/ui/destructive-stacked-left-aligned-modal"
import { FeaturedIcon } from "@/components/ui/featured-icon"
import { Input } from "@/components/ui/input"
import { PaginationPageMinimalCenter } from "@/components/ui/pagination"
import { DashboardLayout } from "@/layouts/dashboard-layout"
import { getPaginationUrl } from "@/lib/navigations/"
import { newPlaylistPath, ownedPlaylistsPath } from "@/routes"
import type { Playlist } from "@/types"
import type { Pagination } from "@/types/contents/pagination"

interface OwnedPlaylistsProps {
  playlists: Playlist[]
  pagination: Pagination
}

export default function OwnedPlaylists({
  playlists,
  pagination,
}: OwnedPlaylistsProps) {
  const [query, setQuery] = useState("")
  const [playlistToDelete, setPlaylistToDelete] = useState<Playlist | null>(
    null,
  )

  /*
   * Returns the owned playlists filtered by the search query when present;
   * otherwise the complete list provided via props.
   */
  const filteredOwnedPlaylists = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    if (!keyword) return playlists
    return playlists.filter((playlist) =>
      playlist.title.toLowerCase().includes(keyword),
    )
  }, [playlists, query])

  const handleDeletePlaylist = (playlist: Playlist) => {
    setPlaylistToDelete(playlist)
  }

  const handleConfirmDeletePlaylist = () => {
    if (!playlistToDelete) return
    router.delete(`/playlists/${playlistToDelete.slug || playlistToDelete.id}`)
    setPlaylistToDelete(null)
  }

  const hasPlaylists = playlists.length > 0

  return (
    <DashboardLayout>
      <Head title="My Playlists" />
      <div className="max-w-container mx-auto flex flex-col gap-8 px-4 pt-8 pb-12 lg:px-8">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-col gap-1">
            <span
              aria-hidden="true"
              className="scotch-rule scotch-rule-action mb-1"
            />
            <h1 className="text-primary font-display text-display-xs md:text-display-sm font-semibold text-balance">
              My Playlists
            </h1>
            <p className="text-md text-tertiary">
              Manage your playlists and curate your collection.
            </p>
          </div>
          <Button href={newPlaylistPath()} size="md" iconLeading={Plus}>
            New playlist
          </Button>
        </header>

        {hasPlaylists ? (
          <section className="flex flex-col gap-6">
            <div className="w-full lg:max-w-xs">
              <Input
                size="sm"
                aria-label="Search playlists"
                placeholder="Search playlists..."
                value={query}
                onChange={setQuery}
                icon={SearchLg}
              />
            </div>

            {filteredOwnedPlaylists.length > 0 ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {/* Animate filtered cards smoothly to their new grid positions. */}
                  <AnimatePresence mode="popLayout" initial={false}>
                    {filteredOwnedPlaylists.map((playlist) => (
                      <motion.div
                        key={playlist.id}
                        layout
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <PlaylistCard
                          playlist={playlist}
                          showActions
                          onDelete={handleDeletePlaylist}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>

                {!query && pagination && pagination.total > 1 && (
                  <PaginationPageMinimalCenter
                    page={pagination.page}
                    total={pagination.total}
                    onPageChange={(page) =>
                      router.visit(getPaginationUrl(ownedPlaylistsPath(), page))
                    }
                  />
                )}
              </>
            ) : (
              <p className="text-tertiary border-secondary rounded-xl border border-dashed p-8 text-center text-sm">
                No playlists match your search. Try a different keyword.
              </p>
            )}
          </section>
        ) : (
          <div className="mx-auto flex w-full max-w-lg flex-col items-center py-12 text-center">
            <FeaturedIcon
              icon={MusicNote01}
              color="brand"
              theme="light"
              size="xl"
            />
            <div className="mt-5 mb-8 flex flex-col items-center gap-2">
              <p className="text-primary text-md font-semibold">
                No playlists yet
              </p>
              <p className="text-md text-tertiary">
                Create your first playlist to start organizing the tracks that
                matter to you.
              </p>
            </div>
            <Button href={newPlaylistPath()} iconLeading={Plus}>
              New playlist
            </Button>
          </div>
        )}
      </div>

      <DestructiveStackedLeftAlignedModal
        isOpen={!!playlistToDelete}
        onOpenChange={(open) => !open && setPlaylistToDelete(null)}
        title={`Delete "${playlistToDelete?.title ?? ""}"?`}
        description="Are you sure you want to delete this playlist? This action cannot be undone."
        onConfirm={handleConfirmDeletePlaylist}
      />
    </DashboardLayout>
  )
}
